export type AnnualLeavePromotionBusinessStatus =
  | "not_notified"
  | "awaiting_signature"
  | "applied"
  | "expired_cancelled"
  | "second_designated";

export type AnnualLeavePromotionRowAction = "send" | "retry" | "review" | "remind" | "reissue";

/**
 * 연차현황 관리 열의 촉진 행 액션 — 상태별 종류·라벨·아이콘·활성 여부.
 * 관리 열은 아이콘 버튼(ds-btn--icon)이라 `label`은 툴팁(title)로만 보이고
 * `icon`이 화면에 그려진다(CS-LOC-260915-0001: 텍스트 버튼이 폭에 잘려 안 보임).
 * 아이콘 이름은 `@hereby/components` 레지스트리(icons.ts)에 있는 것만 — 없는 이름은
 * `$icon()`이 빈 문자열을 돌려줘 버튼이 조용히 사라진다.
 */
export function annualLeavePromotionRowAction(
  promotion?: {
    businessStatus?: unknown;
    processingStatus?: unknown;
    canRetryFirstNotice?: unknown;
  },
  retrying = false,
): { kind: AnnualLeavePromotionRowAction; label: string; icon: string; disabled: boolean } {
  if (promotion?.processingStatus === "retryable_failure") {
    if (promotion.canRetryFirstNotice === true) {
      // 재시도 중엔 버튼 자체가 바쁜 상태 → 버튼용 스피너 아이콘(ic-spinner, 마크업이 animate-spin 부여)
      return retrying
        ? { kind: "retry", label: "재시도 중…", icon: "ic-spinner", disabled: true }
        : { kind: "retry", label: "재시도", icon: "ic-refresh", disabled: false };
    }
    return { kind: "review", label: "검토 필요", icon: "ic-exclamation-circle", disabled: true };
  }
  if (promotion?.businessStatus === "awaiting_signature") {
    return { kind: "remind", label: "재안내", icon: "ic-bell", disabled: false };
  }
  if (promotion?.businessStatus === "expired_cancelled") {
    return { kind: "reissue", label: "다시 발송", icon: "ic-redo", disabled: false };
  }
  if (promotion?.businessStatus === "applied") {
    return { kind: "send", label: "완료 내역", icon: "ic-clipboard-check", disabled: false };
  }
  return { kind: "send", label: "개별 통지", icon: "ic-send", disabled: false };
}

export function annualLeavePromotionStatusLabel(status?: string | null): string {
  switch (status) {
    case "awaiting_signature":
      return "서명 대기";
    case "applied":
      return "제도 적용";
    case "expired_cancelled":
      return "요청 취소(기한 초과)";
    case "second_designated":
      return "2차 지정 완료";
    default:
      return "안내 전";
  }
}

export function annualLeavePromotionPeriodKey(year: number): string {
  return String(year);
}

export function annualLeavePromotionBatchStatusLabel(status?: string | null): string {
  switch (status) {
    case "materializing":
      return "발송 대상 생성 중";
    case "queued":
      return "발송 대기";
    case "running":
      return "발송 중";
    case "cancel_requested":
      return "남은 발송 중지 중";
    case "completed_with_issues":
      return "일부 처리 필요";
    case "completed":
      return "발송 완료";
    case "cancelled":
      return "중지됨";
    case "failed":
      return "발송 실패";
    default:
      return "발송 대기";
  }
}

/**
 * 통지 모달에 고를 수 있는 Sign 서식이 하나도 없을 때의 안내문. 관리 화면 URL 이 있으면
 * 같은 notice 의 [서식 추가하기] 버튼으로 안내하고, 없으면(배포 env 미설정) 전자서명 메뉴로 보낸다.
 * 두 경우 모두 서식을 만든 뒤 [새로고침]으로 돌아오는 경로를 알려 준다(CS-LOC-260914-0002).
 */
export function annualLeavePromotionTemplateEmptyNotice(
  manageUrl: string | null | undefined,
): string {
  const lead = "사용할 수 있는 연차촉진 Sign 서식이 없어요.";
  if (manageUrl) {
    return `${lead} [서식 추가하기]로 Hereby Sign에서 촉진 통지용 서식을 만든 뒤 [새로고침]을 눌러 주세요.`;
  }
  return `${lead} 전자서명 메뉴에서 연동 상태를 확인하고 Hereby Sign에서 촉진 통지용 서식을 만든 뒤 [새로고침]을 눌러 주세요.`;
}
